import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from "swr";
import { useOnboarding } from '../contexts/OnboardingContext';
import { sampleWallets, generateAssetFluctuationData } from '../data/sampleData';
import { apiUrls } from '../utils/apiUtils';
import { fetcher } from '../utils/fetcher';

// Interface for wallet item in API response
interface ApiWallet {
  id: string;
  name: string;
  basicWallet: {
    id: string;
    balance: number;
  } | null;
  creditWallet: {
    id: string;
    balance: number;
  } | null;
  loanWallet: {
    id: string;
    balance: number;
  } | null;
  type: string;
}

// Interface for API response
interface WalletApiResponse {
  data: {
    BASIC?: ApiWallet[];
    CREDIT?: ApiWallet[];
    LOAN?: ApiWallet[];
    [key: string]: ApiWallet[] | undefined;
  };
}

interface TotalAssetsResponse {
  data: number;
}

interface BalanceFluctuation {
  month: string;
  balance: number;
}

interface BalanceFluctuationsResponse {
  walletType: WalletType;
  data: BalanceFluctuation[];
}

interface UseWalletsOptions {
  balanceFluctuationType?: WalletType;
}

/**
 * Hook to manage wallet data and total assets
 * @param options Optional configuration for the hook
 * @returns Wallet groups, totals and helper functions
 */
export const useWallets = (options?: UseWalletsOptions) => {
  const { balanceFluctuationType } = options || {};
  const { t } = useTranslation('wallets');
  const { isOnboardingActive, isSampleDataLoaded } = useOnboarding();
  const useSampleData = isOnboardingActive && isSampleDataLoaded;

  // Fetch wallets data using SWR (skip when using sample data)
  const { data, error, mutate } = useSWR<WalletApiResponse>(
    useSampleData ? null : apiUrls.wallets(),
    fetcher,
    {
      onError: (err) => {
        // Suppress 404 errors during development since API doesn't exist yet
        if (err?.status !== 404) {
          console.error('Wallets API Error:', err);
        }
      },
    }
  );

  // Generate sample wallet groups for onboarding
  const sampleWalletGroups = useMemo((): WalletGroup[] => {
    if (!useSampleData) return [];

    return [
      {
        id: 'basic',
        title: t('types.basic'),
        hasInfo: true,
        wallets: sampleWallets.basic.map(w => ({
          id: w.id,
          name: w.name,
          balance: w.basicWallet.balance,
          type: 'basic',
        })) as unknown as Wallet[],
      },
      {
        id: 'credit',
        title: t('types.credit'),
        hasInfo: true,
        wallets: sampleWallets.credit.map(w => ({
          id: w.id,
          name: w.name,
          balance: w.creditWallet.balance,
          type: 'credit',
        })) as unknown as Wallet[],
      },
      {
        id: 'loan',
        title: t('types.loan'),
        hasInfo: true,
        wallets: sampleWallets.loan.map(w => ({
          id: w.id,
          name: w.name,
          balance: w.loanWallet.balance,
          type: 'loan',
        })) as unknown as Wallet[],
      },
    ];
  }, [useSampleData, t]);

  // Transform API response into wallet groups
  const walletGroups = useMemo(() => {
    // Use sample data during onboarding
    if (useSampleData) {
      return sampleWalletGroups;
    }

    if (!data) return [];

    const groups: WalletGroup[] = [];
    const groupMap: Record<string, WalletGroup> = {};

    // Process the type groupings from the API
    if (data.data) {
      Object.entries(data.data).forEach(([type, wallets]) => {
        // Skip if there are no wallets in this group
        if (!wallets || wallets.length === 0) return;

        // Get group config based on wallet type
        let id = type.toLowerCase();
        let title = type;
        let hasInfo = false;

        // Map types to their translated titles
        if (type === 'BASIC') {
          id = 'basic';
          title = t('types.basic');
          hasInfo = true;
        } else if (type === 'CREDIT') {
          id = 'credit';
          title = t('types.credit');
          hasInfo = true;
        } else if (type === 'LOAN') {
          id = 'loan';
          title = t('types.loan');
          hasInfo = true;
        }

        // Create the group
        const group: WalletGroup = {
          id,
          title,
          hasInfo,
          wallets: [],
        };

        // Add wallets to this group
        wallets.forEach((wallet: ApiWallet) => {
          // Determine balance based on wallet type
          let balance = 0;
          switch (wallet.type) {
            case 'BASIC':
              balance = wallet.basicWallet?.balance || 0;
              break;
            case 'CREDIT':
              balance = wallet.creditWallet?.balance || 0;
              break;
            case 'LOAN':
              balance = wallet.loanWallet?.balance || 0;
              break;
            default:
              balance = 0; // Default to 0 for unknown types
          }

          group.wallets.push({
            id: wallet.id,
            name: wallet.name,
            balance: balance,
            type: wallet.type.toLowerCase(),
          } as Wallet);
        });

        // Add group to our collection
        if (group.wallets.length > 0) {
          groups.push(group);
          groupMap[group.id] = group;
        }
      });
    }

    const sortedGroups = groups
      .sort((a, b) => (a.id === 'basic' ? -1 : 1))
      .filter(group => group.wallets.length > 0);

    return sortedGroups;
  }, [data, t, useSampleData, sampleWalletGroups]);


  /**
   * Calculate total balance for a specific wallet group
   * @param groupId The group ID to calculate total for
   * @returns Total balance for the group
   */
  const getGroupTotal = (groupId: string): number => {
    const group = walletGroups.find(g => g.id === groupId);
    if (!group) return 0;
    return group.wallets.reduce((sum, wallet) => sum + wallet.balance, 0);
  };

  /**
   * Get balance of a specific wallet by ID
   * @param walletId The wallet ID to get balance for
   * @returns The wallet balance or 0 if not found
   */
  const getWalletBalance = (walletId: string): number => {
    for (const group of walletGroups) {
      const wallet = group.wallets.find(w => w.id === walletId);
      if (wallet) {
        return wallet.balance;
      }
    }
    return 0;
  };

  /**
   * Get all wallets with their balances as a flat array
   * @returns Array of all wallets with their details
   */
  const getAllWallets = () => {
    return walletGroups.flatMap(group =>
      group.wallets.map(wallet => ({
        ...wallet,
        groupId: group.id,
        groupTitle: group.title,
      }))
    );
  };

  /**
   * Get wallet details by ID
   * @param walletId The wallet ID to get details for
   * @returns Wallet details including group info or null if not found
   */
  const getWalletById = (walletId: string) => {
    for (const group of walletGroups) {
      const wallet = group.wallets.find(w => w.id === walletId);
      if (wallet) {
        return {
          ...wallet,
          groupId: group.id,
          groupTitle: group.title,
        };
      }
    }
    return null;
  };

  /**
   * Get wallet details by name
   * @param walletName The wallet name to get details for
   * @returns Wallet details including group info or null if not found
   */
  const getWalletByName = (walletName: string) => {
    for (const group of walletGroups) {
      const wallet = group.wallets.find(w => w.name === walletName);
      if (wallet) {
        return {
          ...wallet,
          groupId: group.id,
          groupTitle: group.title,
        };
      }
    }
    return null;
  };

  /**
   * Get all balances grouped by wallet type
   * @returns Object with balances grouped by type
   */
  const getBalancesByType = () => {
    const balances: Record<string, number> = {};

    walletGroups.forEach(group => {
      balances[group.id] = getGroupTotal(group.id);
    });

    return balances;
  };

  // Compute wallet balances grouped by type
  const allWalletBalances = useMemo(() => {
    return walletGroups.reduce((acc, group) => {
      const groupTotal = group.wallets.reduce((sum, wallet) => sum + wallet.balance, 0);

      if (groupTotal !== 0) {
        acc[group.id] = {
          groupId: group.id,
          groupTitle: group.title,
          totalBalance: groupTotal,
        };
      }

      return acc;
    }, {} as Record<string, {
      groupId: string;
      groupTitle: string;
      totalBalance: number;
    }>);
  }, [walletGroups]);

  // fetch total assets (skip when using sample data)
  const { data: totalAssetsData } = useSWR<TotalAssetsResponse>(
    useSampleData ? null : apiUrls.totalAssets(),
    fetcher,
    {
      onError: (err) => {
        // Suppress 404 errors during development since API doesn't exist yet
        if (err?.status !== 404) {
          console.error('Total Assets API Error:', err);
        }
      },
    }
  );

  // total assets
  const totalAssets = useMemo(() => {
    if (useSampleData) {
      // Calculate total from sample wallets
      const basicTotal = sampleWallets.basic.reduce((sum, w) => sum + w.basicWallet.balance, 0);
      const creditTotal = sampleWallets.credit.reduce((sum, w) => sum + w.creditWallet.balance, 0);
      const loanTotal = sampleWallets.loan.reduce((sum, w) => sum + w.loanWallet.balance, 0);
      return basicTotal + creditTotal + loanTotal;
    }
    return totalAssetsData?.data || 0;
  }, [totalAssetsData, useSampleData]);

  // fetch balance fluctuations (skip when using sample data)
  const { data: balanceFluctuationsData } = useSWR<BalanceFluctuationsResponse>(
    useSampleData ? null : apiUrls.balanceFluctuations(balanceFluctuationType),
    fetcher,
    {
      onError: (err) => {
        if (err?.status !== 404) {
          console.error('Balance Fluctuations API Error:', err);
        }
      },
    }
  );

  // balance fluctuations
  const balanceFluctuations = useMemo(() => {
    if (useSampleData) {
      const fluctuationData = generateAssetFluctuationData();
      return {
        walletType: balanceFluctuationType,
        data: fluctuationData.map(item => ({
          month: item.month, // Use ISO date format for proper parsing
          balance: item.amount,
        })),
      };
    }

    if (!balanceFluctuationsData) {
      return {
        walletType: undefined,
        data: [],
      };
    }

    return {
      walletType: balanceFluctuationsData.walletType,
      data: balanceFluctuationsData.data || [],
    };
  }, [balanceFluctuationsData, useSampleData, balanceFluctuationType]);

  return {
    walletGroups,
    totalAssets,
    balanceFluctuations,
    getGroupTotal,
    getWalletBalance,
    getAllWallets,
    getWalletById,
    getWalletByName,
    getBalancesByType,
    allWalletBalances,
    isLoading: useSampleData ? false : (!error && !data),
    isError: error && error?.status !== 404, // Suppress 404 errors in development
    mutate,
  };
};

export default useWallets; 